import { useState } from 'react';
import { FiCpu, FiZap } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';

export default function AgentManagement() {
  const { user } = useAuth();
  const [agentPortalType, setAgentPortalType] = useState('agentic-tools');

  const handleConnectAgentPortal = async () => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/hubspot-oauth/initiate-agent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: agentPortalType })
      });
      if (!res.ok) throw new Error('Failed to initiate OAuth');
      const { redirectUrl } = await res.json();
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('OAuth initiation failed:', err);
      alert('Failed to connect to HubSpot. Please try again.');
    }
  };

  const handleConnectMCPPortal = async () => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/hubspot-oauth/initiate-mcp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to initiate OAuth');
      const { redirectUrl } = await res.json();
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('OAuth initiation failed:', err);
      alert('Failed to connect to HubSpot. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <FiCpu className="w-6 h-6 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Agents</h1>
        </div>
        <p className="text-gray-600">
          Manage your AI agents and their configurations.
        </p>
      </div>

      {/* Agent Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Distribution Explanation Agent Card - Active */}
        <Link
          to="/agents/distribution-explanation"
          className="bg-white rounded-lg border border-gray-200 p-6 hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition-colors duration-200">
              <FiZap className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Distribution Explanation Agent
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Get an explanation of how team selection and owner assignment was made.
          </p>
          <div className="text-blue-600 text-sm font-medium flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            Active
          </div>
        </Link>

        <Link
          to="/agents/configuration"
          className="bg-gray-50 rounded-lg border border-gray-200 p-6 opacity-60 group"
        >
          <div className="flex items-center mb-4">
            <div className="bg-gray-200 p-3 rounded-lg">
              <FiCpu className="w-6 h-6 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-500 mb-2">
            Configuration Agent
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Chat through your use case and get concrete setup steps for teams, rules, routing, and fallback behavior.
          </p>
          <div className="text-gray-400 text-sm font-medium">
            Coming soon
          </div>
        </Link>

        {/* Sales Content Agent Card - Deactivated */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 opacity-70">
          <div className="flex items-center mb-4">
            <div className="bg-gray-200 p-3 rounded-lg">
              <FiZap className="w-6 h-6 text-gray-500" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-500 mb-2">
            Sales Content Agent
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Manage and create sales content with AI assistance. Configure content types and generate materials.
          </p>
          <div className="text-gray-500 text-sm font-medium flex items-center">
            <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
            Deactivated
          </div>
        </div>

        {/* Placeholder for future agents */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 opacity-60">
          <div className="flex items-center mb-4">
            <div className="bg-gray-200 p-3 rounded-lg">
              <FiCpu className="w-6 h-6 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-500 mb-2">
            Additional Agents
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            More AI agents will be available here in future updates.
          </p>
          <div className="text-gray-400 text-sm font-medium">
            Coming soon
          </div>
        </div>

      </div>

      {/* Portal Connections */}
      <div className="mt-12 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Portal Connections</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent Portal
            </label>
            <select
              value={agentPortalType}
              onChange={(e) => setAgentPortalType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
            >
              <option value="agentic-tools">Distributely Agent Tools</option>
              <option value="revops-audit">RevOps Audit</option>
            </select>
            <button
              onClick={handleConnectAgentPortal}
              className="mt-4 w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              Connect Agent Portal
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              MCP Portal
            </label>
            <button
              onClick={handleConnectMCPPortal}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              Connect MCP Portal
            </button>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <FiCpu className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              Agent Management
            </h3>
            <p className="text-sm text-blue-700">
              This agent management panel provides access to AI-powered tools for sales and discovery processes. 
              Start with the Distribution Explanation Agent to review assignment decisions. 
              Additional agents will be added to expand your AI capabilities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
